import { ShareNetworkIcon as ShareIcon } from "@wags/ui/product-icons.tsx";

import { useProfilePageDataEnabled } from "./profile-page-flag";
/* eslint-disable react-doctor/no-giant-component, react-doctor/use-lazy-motion, react-hooks-js/refs */
import { useDbClient } from "@tanstack/react-db";
import { useQuery } from "@tanstack/react-query";
import { getRouteApi, useNavigate } from "@tanstack/react-router";
import { Button } from "@wags/ui/button.tsx";
import { ImageCarouselDialog } from "@wags/ui/image-carousel-dialog.tsx";
import { cn } from "@wags/ui/ui-utils";
import {
    formatDistance,
    getOrganizationType,
    getProviderType,
    ProviderType,
    pickPreferredImageMedia,
} from "@wags/catalog/pure.ts";
import { useReducedMotion, type MotionProps } from "framer-motion";
import { usePostHog } from "posthog-js/react";
import { useRef, useSyncExternalStore } from "react";
import {
    Breadcrumb,
    useBackNavigation,
    usePhotoCarousel,
    whenIdle,
} from "@wags/tanstack-start/browser";
import { KitsCopilotPageContext } from "@/features/kits-conversations";
import { MascotAvatar } from "@/features/kits-conversations";
import {
    PageErrorState,
    SectionErrorState,
} from "@/components/shared/ErrorState";
import { Header } from "@/components/Header";
import { NotFoundState } from "@/components/shared/NotFound";
import { BreederListingsBlock } from "./provider-listings/Breeder/BreederListingsBlock";
import {
    providerCardsFromReferences,
    type ProviderDogListingCard,
} from "./provider-listings/provider-listing-card";
import {
    getProviderLoadState,
    type ProviderLoadState,
} from "./provider-load-state";
import { RecentlyAdoptedSection } from "./provider-listings/RecentlyAdoptedSection";
import { AvailableAnimalsSection } from "./provider-listings/Shelter/AvailableAnimalsSection";
import { resolveMaxDistanceMiles } from "@wags/discovery/pure.ts";
import { MobilePhotoCarousel } from "@/components/shared";
import { iconButtonClass } from "@wags/ui/icon-button-class.ts";
import { StickyMobileHeader } from "@wags/ui/sticky-mobile-header.tsx";
import { shareOrCopy } from "@wags/ui/share-or-copy.ts";
import { useProviderSectionData } from "./use-provider-section-data";
import { ClaimEntry } from "./claim-entry";
import {
    PROVIDER_BREADCRUMB_CLASS,
    PROVIDER_CHROME_CLASS,
    PROVIDER_SHEET_CLASS,
    PROVIDER_SHELL_CLASS,
    ProviderSheetSentinel,
} from "./provider-sheet";
import { useKitsCopilotEnabled } from "@/features/kits-conversations";
import {
    requestOpenChat,
    usePublishViewState,
} from "@/features/kits-conversations";
import { usePrefetchListings } from "@/features/listings";
import { useWagsListingsLiveQuery } from "@/features/listings";
import { PROVIDER_STATIC_MAP, staticMapSrc } from "@/features/viewer-location";
import { useGooglePlaces } from "@/features/viewer-location";
import { calculateDistance, useUserLocation } from "@/features/viewer-location";
import {
    AvailabilityFreshness,
    AboutSection,
    hasPricingContent,
    hasShelterOperationsContent,
    hasTrustFactsContent,
    hasWhatToExpectContent,
    mapOrgReviewToReview,
    reviewCountLabel,
    MeetTheProviderSection,
    MissionSection,
    normalizeExternalUrl,
    prefetchProviders,
    PricingSection,
    ProviderDetailsSection,
    NearbyProvidersSection,
    ProgramsSection,
    ProviderHeader,
    ProviderLoadingSkeleton,
    ProviderPageShell,
    type ProviderMetaStat,
    ProviderPhotoStrip,
    ProviderReviewsSection,
    ReputationThemesSection,
    ShelterCta,
    ShelterHero,
    ShelterMobileAction,
    ShelterOperationsSection,
    ShelterWhatToExpectSection,
    TrustFactsSection,
    useNearbyProvidersLiveQuery,
    useProviderListingReferencesInitialPage,
    useProviderProfileV2,
    reviewNoun,
    useProviders,
    useWagsOrganizationsReviewsLiveQuery,
    WhatToExpectSection,
} from "@/features/providers";
import { mountEntrance } from "@wags/ui/motion.ts";
import type { ProviderHeadData } from "./provider-route-data";

type ProviderOrg = NonNullable<ReturnType<typeof useProviders>["data"]>[number];

function deriveProviderData(
    breeder: ProviderOrg | undefined,
    reverseGeocodedLocation: { displayName?: string | null } | null | undefined,
    userLocation: { lat: number; lon: number } | null,
    isUserLocationLoading: boolean,
) {
    const identity = (() => {
        const providerType = getProviderType(breeder?.type);
        const realKennelName =
            breeder?.kennel_name?.trim() || breeder?.name?.trim();
        const kennelName = realKennelName || "Breeder profile";
        const breederContactName = breeder?.breeder_name?.trim();
        const breederName = breederContactName || kennelName;
        const breedNames = breeder?.breedNames ?? [];
        const breedList =
            breedNames.length === 0
                ? "dogs"
                : breedNames.length === 1
                  ? breedNames[0]
                  : breedNames.length === 2
                    ? `${breedNames[0]} and ${breedNames[1]}`
                    : `${breedNames.slice(0, -1).join(", ")}, and ${breedNames[breedNames.length - 1]}`;
        return {
            providerType,
            kennelName,
            realProviderName: realKennelName,
            breederContactName,
            breederName,
            breedList,
            breedSlugs: [...new Set(breeder?.breedSlugs ?? [])],
            locationDisplay: [breeder?.city, breeder?.state]
                .map((v) => v?.trim())
                .filter((v): v is string => Boolean(v))
                .join(", "),
        };
    })();
    const {
        providerType,
        kennelName,
        realProviderName,
        breederContactName,
        breederName,
        breedList,
        breedSlugs,
        locationDisplay,
    } = identity;

    const breederLatitude = breeder?.location?.y;
    const breederLongitude = breeder?.location?.x;
    const hasBreederCoordinates =
        Number.isFinite(breederLatitude) && Number.isFinite(breederLongitude);
    const realLocationLabel =
        reverseGeocodedLocation?.displayName || locationDisplay || undefined;
    const meetupLocationLabel = realLocationLabel || "Location unavailable";
    const distanceMiles =
        hasBreederCoordinates && userLocation
            ? calculateDistance(
                  userLocation.lat,
                  userLocation.lon,
                  breederLatitude ?? 0,
                  breederLongitude ?? 0,
              )
            : null;
    const distanceLabel = isUserLocationLoading
        ? "Calculating distance…"
        : (formatDistance(distanceMiles, "narrative") ??
          "Distance unavailable");
    const providerMapSrc = hasBreederCoordinates
        ? (staticMapSrc(
              breederLatitude,
              breederLongitude,
              PROVIDER_STATIC_MAP,
          ) ?? null)
        : null;

    const media = (() => {
        const seenMediaUrls = new Set<string>();
        const breederMediaItems =
            breeder?.media.filter(
                (item): item is typeof item & { url: string } => {
                    if (!item.url || seenMediaUrls.has(item.url)) return false;
                    seenMediaUrls.add(item.url);
                    return true;
                },
            ) ?? [];
        const coverPhotoUrl = breeder?.coverPhotoUrl?.trim() || undefined;
        const profileImageUrl =
            coverPhotoUrl ??
            pickPreferredImageMedia(
                breederMediaItems,
                breeder?.primaryPhotoId ?? null,
            )?.url;
        const featuredBreederMediaItem =
            breederMediaItems.find((item) => item.url !== profileImageUrl) ??
            breederMediaItems[0];
        const logoLikeMediaUrl = profileImageUrl ?? breederMediaItems[0]?.url;
        return {
            breederMediaItems,
            profileImageUrl,
            meetBreederImageUrl: coverPhotoUrl ?? featuredBreederMediaItem?.url,
            galleryMediaItems: profileImageUrl
                ? breederMediaItems
                : breederMediaItems.filter(
                      (item) => item.url !== logoLikeMediaUrl,
                  ),
        };
    })();
    const {
        breederMediaItems,
        profileImageUrl,
        meetBreederImageUrl,
        galleryMediaItems,
    } = media;
    const breederDescription = (() => {
        const breederIntro = `Meet ${breederName}${breederContactName ? ` from ${kennelName}` : ""}${locationDisplay ? ` based in ${locationDisplay}` : ""}.`;
        return [breederIntro, breeder?.description?.trim()]
            .filter((v): v is string => Boolean(v))
            .join("\n\n");
    })();

    const { certifications, inBusinessSinceYear } = (() => {
        const certifications: string[] = [];
        if (breeder?.hasAkcRegistration)
            certifications.push("Registered AKC breeder");
        return {
            certifications,
            inBusinessSinceYear: breeder?.inBusinessSince
                ? new Date(breeder.inBusinessSince).getFullYear()
                : null,
        };
    })();

    return {
        providerType,
        kennelName,
        realProviderName,
        realLocationLabel,
        breederName,
        breedList,
        breedSlugs,
        locationDisplay,
        distanceLabel,
        staticMapSrc: providerMapSrc,
        isVerified: Boolean(breeder?.isVerified),
        meetupLocationLabel,
        breederMediaItems,
        galleryMediaItems,
        profileImageUrl,
        meetBreederImageUrl,
        breederDescription,
        certifications,
        inBusinessSinceYear,
    };
}

type OrgReview = NonNullable<
    ReturnType<typeof useWagsOrganizationsReviewsLiveQuery>["data"]
>["reviews"][number];

function mapReviewToAdoptionStory(r: OrgReview) {
    return mapOrgReviewToReview(r, "an animal");
}

function deriveShelterData(
    breeder: ProviderOrg | undefined,
    reviewsData: ReturnType<
        typeof useWagsOrganizationsReviewsLiveQuery
    >["data"],
) {
    const organizationType = getOrganizationType(breeder?.type);

    const applicationUrl = normalizeExternalUrl(breeder?.website);
    const missionStatement = breeder?.missionStatement?.trim() || null;
    const adoptionStories =
        reviewsData?.reviews.map(mapReviewToAdoptionStory) ?? [];
    return {
        organizationType,
        applicationUrl,
        missionStatement,
        adoptionStories,
    };
}

function providerStats(
    reviewCount: number,
    sinceYear: number | null,
    since: { label: string; linePrefix: string },
): ProviderMetaStat[] {
    const reviewUnit = reviewNoun(reviewCount);
    return [
        ...(reviewCount > 0
            ? [
                  {
                      value: reviewCount.toLocaleString(),
                      label: reviewUnit,
                      line: reviewCountLabel(reviewCount),
                  },
              ]
            : []),
        ...(sinceYear
            ? [
                  {
                      value: String(sinceYear),
                      label: since.label,
                      line: `${since.linePrefix} ${sinceYear}`,
                  },
              ]
            : []),
    ];
}

const routeApi = getRouteApi("/provider/$providerId");

const READING_COL = "w-full";

function SectionDivider() {
    return <div className={`my-10 h-px bg-doberman-black/8 ${READING_COL}`} />;
}

function locationsOf(provider: ProviderOrg | undefined) {
    return provider?.locations ?? [];
}

function getProviderRecord(breederData: ProviderOrg[] | undefined) {
    const breeder = breederData?.[0];
    return {
        breeder,
        publishedName: breeder?.name?.trim().slice(0, 80) || undefined,
        providerType: getProviderType(breeder?.type),
    };
}

let providerCardsIdle = false;
const providerCardsIdleListeners = new Set<() => void>();
let providerCardsIdleScheduled = false;

function subscribeProviderCardsIdle(listener: () => void) {
    providerCardsIdleListeners.add(listener);
    if (!providerCardsIdleScheduled && !providerCardsIdle) {
        providerCardsIdleScheduled = true;
        whenIdle(() => {
            providerCardsIdle = true;
            for (const idleListener of providerCardsIdleListeners) {
                idleListener();
            }
        });
    }
    return () => {
        providerCardsIdleListeners["delete"](listener);
    };
}

function useProviderCardsIdle() {
    return useSyncExternalStore(
        subscribeProviderCardsIdle,
        () => providerCardsIdle,
        () => false,
    );
}

function visibleProviderListingIds(
    idle: boolean,
    references: ReadonlyArray<{ listingId: string }> | undefined,
) {
    if (!idle) return [];
    return (references ?? []).map((reference) => reference.listingId);
}

function providerRouteFallback({
    loadState,
    providerId,
    headName,
    head,
    summaryCards,
    listingTotal,
    listingAvailableCount,
    updateRef,
    ownerRef,
    entrance,
    onRetry,
    onFind,
}: {
    loadState: ProviderLoadState;
    providerId: string;
    headName: string | null;
    head: ProviderHeadData;
    summaryCards: ProviderDogListingCard[];
    listingTotal: number | undefined;
    listingAvailableCount: number | undefined;
    updateRef: (node: HTMLSpanElement | null) => void;
    ownerRef: (node: HTMLSpanElement | null) => (() => void) | undefined;
    entrance: (delay?: number) => MotionProps;
    onRetry: () => Promise<void>;
    onFind: () => void;
}) {
    const markers = (
        <>
            <span ref={updateRef} hidden />
            <span ref={ownerRef} hidden />
        </>
    );
    switch (loadState) {
        case "ready":
            return null;
        case "error":
            return (
                <ProviderPageShell>
                    {markers}
                    <Header className="bg-maltese-white" hideScrollBorder />
                    <PageErrorState
                        className="min-h-below-header"
                        title="This provider didn't load"
                        description="Their dogs and details are temporarily unavailable. Try loading the page again."
                        scene="searching"
                        onRetry={onRetry}
                    />
                </ProviderPageShell>
            );
        case "loading":
            return (
                <>
                    {markers}
                    <ProviderLoadingSkeleton />
                </>
            );
        case "preview": {
            const previewLocation = [head?.city, head?.state]
                .map((value) => value?.trim())
                .filter(Boolean)
                .join(", ");
            return (
                <ProviderPageShell>
                    {markers}
                    <Header
                        className="hidden md:block bg-maltese-white"
                        hideScrollBorder
                    />
                    <main className="page-container pt-5.5 pb-12">
                        <ShelterHero
                            organizationName={headName ?? "Provider"}
                            organizationType={getOrganizationType(undefined)}
                            locationDisplay={previewLocation}
                            stats={[]}
                            entrance={entrance}
                        />
                        <div className="mt-10">
                            <AvailableAnimalsSection
                                animalCards={summaryCards}
                                providerId={providerId}
                                totalAnimalCount={listingTotal ?? 0}
                                availableCount={listingAvailableCount ?? 0}
                            />
                        </div>
                    </main>
                </ProviderPageShell>
            );
        }
        case "not-found":
            return (
                <ProviderPageShell>
                    {markers}
                    <Header className="bg-maltese-white" hideScrollBorder />
                    <main>
                        <NotFoundState
                            className="min-h-below-header"
                            title="This provider isn't here"
                            description="They may have moved on, or this link may be outdated."
                            scene="searching"
                            actions={
                                <Button
                                    size="lg"
                                    emphasis="cta"
                                    onClick={onFind}
                                >
                                    Find providers
                                </Button>
                            }
                        />
                    </main>
                </ProviderPageShell>
            );
        default: {
            const unreachable: never = loadState;
            return unreachable;
        }
    }
}

export function ProviderRoutePage() {
    const { providerId } = routeApi.useParams();
    const loaderData = routeApi.useLoaderData();
    const headName = loaderData.head?.name?.trim() || null;
    const dbClient = useDbClient();
    const navigate = useNavigate();
    const handleBack = useBackNavigation();
    const { location: userLocation, loading: isUserLocationLoading } =
        useUserLocation();
    const posthog = usePostHog();

    const { data: breederData, isError: breederIsError } = useProviders([
        providerId,
    ]);
    const {
        breeder,
        publishedName,
        providerType: initialProviderType,
    } = getProviderRecord(breederData);
    const providerViewStatePublication = usePublishViewState(
        "provider",
        {
            providerId,
            ...(publishedName === undefined ? {} : { name: publishedName }),
        },
        { leadingEdge: true },
    );

    const profilePageDataEnabled = useProfilePageDataEnabled();
    const profile = useProviderProfileV2(
        providerId,
        initialProviderType,
        profilePageDataEnabled,
    );

    const {
        reputationShown,
        trustFactsShownShelter,
        trustFactsShownBreeder,
        pricingShown,
        shelterOperationsShown,
    } = (() => ({
        reputationShown:
            !!profile &&
            profile.reputation.themes.positives.length +
                profile.reputation.themes.negatives.length >
                0,
        trustFactsShownShelter: !!profile && hasTrustFactsContent(profile),
        trustFactsShownBreeder:
            !!profile && hasTrustFactsContent(profile, ["operations"]),
        pricingShown: !!profile && hasPricingContent(profile.pricing),
        shelterOperationsShown: hasShelterOperationsContent(
            profile?.operations,
        ),
    }))();
    const {
        data: providerListingPage,
        isError: providerListingsIsError,
        refetch: refetchProviderListings,
    } = useProviderListingReferencesInitialPage(providerId);
    const providerListingIds = visibleProviderListingIds(
        useProviderCardsIdle(),
        providerListingPage?.listingReferences,
    );
    usePrefetchListings(providerListingIds);
    const { data: hydratedProviderListings } =
        useWagsListingsLiveQuery(providerListingIds);
    const hydratedProviderListingById = new Map(
        (hydratedProviderListings ?? []).map((listing) => [
            listing.id,
            listing,
        ]),
    );
    const {
        data: reviewsData,
        isError: reviewsIsError,
        refetch: refetchReviews,
    } = useWagsOrganizationsReviewsLiveQuery(providerId, { limit: 5 });
    const providerLocations = locationsOf(breeder);
    const breederCoords = (() => {
        const breederLat = breeder?.location?.y;
        const breederLng = breeder?.location?.x;
        return Number.isFinite(breederLat) &&
            Number.isFinite(breederLng) &&
            breederLat != null &&
            breederLng != null
            ? { lat: breederLat, lng: breederLng }
            : null;
    })();
    const {
        data: nearbyProvidersData,
        isError: nearbyProvidersIsError,
        refetch: refetchNearbyProviders,
    } = useNearbyProvidersLiveQuery(breederCoords, {
        excludeOrganizationId: providerId,
    });

    const screeningFacts = profile?.factGroups.find(
        (group) => group.category === "operations",
    )?.facts;
    const whatToExpectShown = hasWhatToExpectContent({
        coordinates: breederCoords,
        screeningFacts,
    });

    const { data: provider } = useProviderSectionData(providerId);
    const { reverseGeocode } = useGooglePlaces();
    const reverseGeocodeQuery = (() => {
        const latitude = breeder?.location?.y;
        const longitude = breeder?.location?.x;
        return {
            queryKey: [
                "provider-reverse-geocode",
                providerId,
                latitude,
                longitude,
            ],
            queryFn: () => reverseGeocode(latitude ?? 0, longitude ?? 0),
            enabled: Number.isFinite(latitude) && Number.isFinite(longitude),
            staleTime: 1000 * 60 * 60 * 24,
        };
    })();
    const { data: reverseGeocodedLocation } = useQuery(reverseGeocodeQuery);

    const prefersReducedMotion = useReducedMotion();
    const {
        isOpen: isPhotoCarouselOpen,
        startIndex: photoCarouselStartIndex,
        open: openPhotoCarouselAtIndex,
        onOpenChange: setIsPhotoCarouselOpen,
    } = usePhotoCarousel();
    const copilotEnabled = useKitsCopilotEnabled();

    const entrance = (delay = 0) => mountEntrance(prefersReducedMotion, delay);
    const trackedProviderRef = useRef<string | null>(null);

    const recordProviderView = (node: HTMLDivElement | null) => {
        if (!node || !breeder || trackedProviderRef.current === providerId)
            return;
        trackedProviderRef.current = providerId;
        posthog.capture("provider_viewed", {
            providerId,
            providerName: breeder.name,
            providerType: breeder.type,
            source: breeder.source,
        });
    };

    const requirementsReady = !!breeder && !!providerListingPage;
    const requirementsAnchorRef = (node: HTMLElement | null) => {
        if (!node || !requirementsReady) return;
        if (window.location.hash !== "#requirements") return;

        const scroll = () => node.scrollIntoView({ block: "start" });
        scroll();
        const observer = new ResizeObserver(scroll);
        observer.observe(document.body);
        const stop = () => observer.disconnect();
        const settled = setTimeout(stop, 6000);
        window.addEventListener("wheel", stop, { once: true, passive: true });
        window.addEventListener("touchstart", stop, {
            once: true,
            passive: true,
        });
        window.addEventListener("keydown", stop, { once: true });
        return () => {
            clearTimeout(settled);
            stop();
            window.removeEventListener("wheel", stop);
            window.removeEventListener("touchstart", stop);
            window.removeEventListener("keydown", stop);
        };
    };

    const summaryCards = providerCardsFromReferences(
        providerListingPage?.listingReferences ?? [],
        hydratedProviderListingById,
        "Dog",
    );
    const adoptedCards = providerCardsFromReferences(
        providerListingPage?.recentlyAdoptedListingReferences ?? [],
        hydratedProviderListingById,
        "Dog",
    );
    const loadState = getProviderLoadState({
        breederIsError,
        hasBreederData: breederData != null,
        hasBreeder: breeder != null,
        listingsIsError: providerListingsIsError,
        hasListingPage: providerListingPage != null,
        hasHeadName: headName != null,
    });
    const routeFallback = providerRouteFallback({
        loadState,
        providerId,
        headName,
        head: loaderData.head,
        summaryCards,
        listingTotal: providerListingPage?.total,
        listingAvailableCount: providerListingPage?.availableCount,
        updateRef: providerViewStatePublication.updateRef,
        ownerRef: providerViewStatePublication.ownerRef,
        entrance,
        onRetry: async () => {
            await Promise.all([
                prefetchProviders(dbClient, [providerId]),
                refetchProviderListings(),
            ]);
        },
        onFind: () => navigate({ to: "/find" }),
    });
    if (routeFallback) return routeFallback;
    const loadedProviderListingPage = providerListingPage!;

    const {
        providerType,
        kennelName,
        realProviderName,
        realLocationLabel,
        breederName,
        breedList,
        breedSlugs,
        locationDisplay,
        distanceLabel,
        staticMapSrc,
        isVerified,
        meetupLocationLabel,
        breederMediaItems,
        galleryMediaItems,
        profileImageUrl,
        meetBreederImageUrl,
        breederDescription,
        certifications: batchCertifications,
        inBusinessSinceYear,
    } = deriveProviderData(
        breeder,
        reverseGeocodedLocation,
        userLocation,
        isUserLocationLoading,
    );
    const claimEntry = (
        <ClaimEntry
            providerId={providerId}
            providerName={realProviderName}
            providerType={providerType}
            photoUrl={profileImageUrl}
            locationLabel={realLocationLabel}
            className="mt-6"
            enabled={copilotEnabled}
            isVerified={isVerified}
        />
    );

    const {
        certifications,
        providerWebsite,
        reviewsErrorState,
        nearbyProvidersErrorState,
        hasReviews,
        hasReviewsSection,
        headerRating,
    } = (() => {
        const hasReviews = !!reviewsData && reviewsData.reviews.length > 0;
        const hasReviewsSection = reputationShown || hasReviews;
        return {
            hasReviews,
            hasReviewsSection,
            headerRating: {
                averageRating: profile?.reputation.averageRating,
                ratingCount: profile?.reputation.ratingCount,
                reviewsHref: hasReviewsSection ? "#reviews" : undefined,
            },
            certifications: profile?.identity.certifications.length
                ? profile.identity.certifications
                : batchCertifications,
            providerWebsite:
                normalizeExternalUrl(
                    profile?.identity.websiteUrl ?? breeder?.website,
                ) ?? undefined,
            reviewsErrorState: reviewsIsError && !reviewsData && (
                <>
                    <SectionDivider />
                    <SectionErrorState
                        title="Reviews didn't load"
                        description="Try loading the reviews again."
                        onRetry={() => refetchReviews()}
                    />
                </>
            ),
            nearbyProvidersErrorState:
                nearbyProvidersIsError && !nearbyProvidersData ? (
                    <>
                        <SectionDivider />
                        <SectionErrorState
                            title={
                                providerType === ProviderType.Breeder
                                    ? "Similar providers didn't load"
                                    : "Nearby shelters didn't load"
                            }
                            description={
                                providerType === ProviderType.Breeder
                                    ? "Try loading nearby providers again."
                                    : "Try loading nearby shelters again."
                            }
                            onRetry={() => refetchNearbyProviders()}
                        />
                    </>
                ) : null,
        };
    })();

    const renderShelter = () => {
        const organizationName = kennelName;
        const {
            organizationType,
            missionStatement: batchMissionStatement,
            adoptionStories,
        } = deriveShelterData(breeder, reviewsData);

        const missionStatement =
            profile?.identity.mission ?? batchMissionStatement;
        const description = breeder?.description?.trim();
        const availabilityFreshness = profile && (
            <AvailabilityFreshness
                availability={profile.availability}
                listingCount={loadedProviderListingPage.availableCount}
            />
        );

        const applicationUrl = normalizeExternalUrl(
            profile?.identity.applicationUrl,
        );
        const isApplicationPage = applicationUrl != null;

        const shelterWebsite =
            providerWebsite &&
            normalizeExternalUrl(providerWebsite) === applicationUrl
                ? undefined
                : providerWebsite;

        const shelterStats = providerStats(
            reviewsData?.total ?? 0,
            inBusinessSinceYear,
            { label: "saving since", linePrefix: "Saving animals since" },
        );

        return (
            <ProviderPageShell
                ref={recordProviderView}
                className={PROVIDER_SHELL_CLASS}
            >
                {(() => (
                    <>
                        <span
                            ref={providerViewStatePublication.updateRef}
                            hidden
                        />
                        <span
                            ref={providerViewStatePublication.ownerRef}
                            hidden
                        />
                        <Header
                            className={cn(
                                "hidden md:block",
                                PROVIDER_CHROME_CLASS,
                            )}
                            data-sheet-chrome
                            hideScrollBorder
                        />

                        <div className="hidden md:block pt-header" />
                        <div
                            data-sheet-breadcrumb
                            className={cn(
                                "hidden md:sticky md:top-17.5 md:z-40 md:block",
                                PROVIDER_BREADCRUMB_CLASS,
                            )}
                        >
                            <Breadcrumb
                                items={[
                                    { label: "Find", to: "/find" },
                                    {
                                        label: "Shelters & Rescues",
                                        onClick: () => {
                                            if (userLocation) {
                                                navigate({
                                                    to: "/find",
                                                    search: {
                                                        lat: userLocation.lat,
                                                        lng: userLocation.lon,
                                                        providerType:
                                                            "shelter_rescue",
                                                        maxDistanceMiles:
                                                            resolveMaxDistanceMiles(),
                                                        page: 0,
                                                        limit: 50,
                                                    },
                                                });
                                            } else {
                                                navigate({ to: "/find" });
                                            }
                                        },
                                    },
                                    { label: organizationName },
                                ]}
                            />
                        </div>

                        <StickyMobileHeader onBack={handleBack} surface="sheet">
                            <button
                                type="button"
                                aria-label="Share this profile"
                                className={iconButtonClass}
                                onClick={() => shareOrCopy(organizationName)}
                            >
                                <ShareIcon className="size-6" />
                            </button>
                        </StickyMobileHeader>
                    </>
                ))()}

                <div className="page-container pt-5.5 pb-12 md:pt-4 md:pb-40 flex flex-col">
                    <ShelterHero
                        organizationName={organizationName}
                        isVerified={isVerified}
                        organizationType={organizationType}
                        locationDisplay={locationDisplay}
                        stats={shelterStats}
                        website={shelterWebsite}
                        entrance={entrance}
                        cta={
                            applicationUrl ? (
                                <ShelterCta
                                    applicationUrl={applicationUrl}
                                    isApplicationPage={isApplicationPage}
                                    organizationName={organizationName}
                                    size="wide"
                                />
                            ) : undefined
                        }
                    />
                    {claimEntry}

                    {galleryMediaItems.length > 0 && (
                        <div className="mt-6 md:hidden">
                            <MobilePhotoCarousel
                                mediaItems={galleryMediaItems}
                                alt={organizationName}
                                onImageClick={openPhotoCarouselAtIndex}
                                indicator="dots"
                                confined
                            />
                        </div>
                    )}

                    <div
                        className={cn(
                            "mt-10 flex flex-col",
                            PROVIDER_SHEET_CLASS,
                        )}
                    >
                        <ProviderSheetSentinel />
                        {(() => (
                            <>
                                <ProviderPhotoStrip
                                    mediaItems={galleryMediaItems}
                                    alt={organizationName}
                                    onPhotoClick={openPhotoCarouselAtIndex}
                                    className="mb-10"
                                />
                                <div className="mb-5 hidden md:block">
                                    <h2 className="text-section-title text-doberman-black text-balance">
                                        Available dogs
                                    </h2>
                                    {availabilityFreshness && (
                                        <div className="mt-2">
                                            {availabilityFreshness}
                                        </div>
                                    )}
                                </div>
                                <AvailableAnimalsSection
                                    mobileDescription={availabilityFreshness}
                                    animalCards={summaryCards}
                                    providerId={providerId}
                                    totalAnimalCount={
                                        loadedProviderListingPage.total
                                    }
                                    availableCount={
                                        loadedProviderListingPage.availableCount
                                    }
                                    fallbackImageUrl={profileImageUrl}
                                />
                                <RecentlyAdoptedSection
                                    className="mt-12"
                                    cards={adoptedCards}
                                />
                            </>
                        ))()}

                        {(() => (
                            <>
                                {missionStatement && (
                                    <>
                                        <SectionDivider />
                                        <div className={READING_COL}>
                                            <MissionSection
                                                providerId={providerId}
                                                missionStatement={
                                                    missionStatement
                                                }
                                            />
                                        </div>
                                    </>
                                )}

                                {description && (
                                    <>
                                        <SectionDivider />
                                        <div className={READING_COL}>
                                            <AboutSection
                                                providerId={providerId}
                                                description={description}
                                            />
                                        </div>
                                    </>
                                )}

                                <ProviderDetailsSection provider={breeder} />

                                {profile && (
                                    <>
                                        {trustFactsShownShelter && (
                                            <>
                                                <SectionDivider />
                                                <div
                                                    id="requirements"
                                                    ref={requirementsAnchorRef}
                                                    className={cn(
                                                        READING_COL,
                                                        "scroll-mt-24",
                                                    )}
                                                >
                                                    <TrustFactsSection
                                                        profile={profile}
                                                        askAction={{
                                                            icon: (
                                                                <MascotAvatar
                                                                    size={22}
                                                                    animated={
                                                                        false
                                                                    }
                                                                />
                                                            ),
                                                            onClick:
                                                                requestOpenChat,
                                                        }}
                                                    />
                                                </div>
                                            </>
                                        )}
                                        {pricingShown && (
                                            <>
                                                <SectionDivider />
                                                <div className={READING_COL}>
                                                    <PricingSection
                                                        pricing={
                                                            profile.pricing
                                                        }
                                                    />
                                                </div>
                                            </>
                                        )}
                                        {profile.programs &&
                                            profile.programs.length > 0 && (
                                                <>
                                                    <SectionDivider />
                                                    <div
                                                        className={READING_COL}
                                                    >
                                                        <ProgramsSection
                                                            programs={
                                                                profile.programs
                                                            }
                                                        />
                                                    </div>
                                                </>
                                            )}
                                        {shelterOperationsShown &&
                                            profile.operations && (
                                                <>
                                                    <SectionDivider />
                                                    <div
                                                        className={READING_COL}
                                                    >
                                                        <ShelterOperationsSection
                                                            operations={
                                                                profile.operations
                                                            }
                                                        />
                                                    </div>
                                                </>
                                            )}
                                    </>
                                )}

                                <SectionDivider />
                                <div
                                    id={
                                        trustFactsShownShelter
                                            ? undefined
                                            : "requirements"
                                    }
                                    className={cn(READING_COL, "scroll-mt-24")}
                                >
                                    <ShelterWhatToExpectSection
                                        locationDisplay={locationDisplay}
                                        staticMapSrc={staticMapSrc}
                                        phone={breeder?.phone}
                                        email={breeder?.email}
                                        coordinates={breederCoords}
                                        locations={providerLocations}
                                    />
                                </div>

                                {profile && reputationShown && (
                                    <>
                                        <SectionDivider />
                                        <div className={READING_COL}>
                                            <ReputationThemesSection
                                                reputation={profile.reputation}
                                                storiesBelow={
                                                    adoptionStories.length > 0
                                                }
                                            />
                                        </div>
                                    </>
                                )}

                                {reviewsErrorState}

                                {adoptionStories.length > 0 && (
                                    <>
                                        {!reputationShown && <SectionDivider />}
                                        <div
                                            className={
                                                reputationShown ? "mt-8" : ""
                                            }
                                        >
                                            <ProviderReviewsSection
                                                reviews={adoptionStories}
                                                reviewCount={
                                                    reviewsData?.total ??
                                                    adoptionStories.length
                                                }
                                                organizationId={providerId}
                                                showHeading={!reputationShown}
                                            />
                                        </div>
                                    </>
                                )}
                            </>
                        ))()}

                        {(() => (
                            <>
                                {nearbyProvidersErrorState}

                                {nearbyProvidersData &&
                                    nearbyProvidersData.providers.length >
                                        0 && (
                                        <>
                                            <SectionDivider />
                                            <NearbyProvidersSection
                                                providers={
                                                    nearbyProvidersData.providers
                                                }
                                                city={
                                                    breeder?.city ??
                                                    locationDisplay
                                                }
                                            />
                                        </>
                                    )}

                                <div className="h-9 md:hidden" />
                            </>
                        ))()}
                    </div>
                </div>

                {(() => (
                    <>
                        {applicationUrl && !copilotEnabled && (
                            <ShelterMobileAction
                                applicationUrl={applicationUrl}
                                isApplicationPage={isApplicationPage}
                                organizationName={organizationName}
                            />
                        )}
                        {copilotEnabled && (
                            <KitsCopilotPageContext
                                contactPreview={{
                                    provider: {
                                        name: organizationName,
                                        type: providerType,
                                        isVerified,
                                    },
                                }}
                                transaction={
                                    applicationUrl
                                        ? {
                                              kind: "link",
                                              link: {
                                                  href: applicationUrl,
                                                  type: isApplicationPage
                                                      ? "application"
                                                      : "website",
                                                  label: isApplicationPage
                                                      ? "Start application"
                                                      : "Visit their site",
                                              },
                                          }
                                        : undefined
                                }
                            />
                        )}

                        {galleryMediaItems.length > 0 && (
                            <ImageCarouselDialog
                                mediaItems={galleryMediaItems}
                                open={isPhotoCarouselOpen}
                                onOpenChange={setIsPhotoCarouselOpen}
                                alt={organizationName}
                                initialIndex={photoCarouselStartIndex}
                            />
                        )}
                    </>
                ))()}
            </ProviderPageShell>
        );
    };

    if (providerType !== ProviderType.Breeder) return renderShelter();

    const breedCrumbs =
        breedSlugs.length > 0
            ? [
                  {
                      label: breedList,
                      onClick: () => {
                          const lat = userLocation?.lat ?? breederCoords?.lat;
                          const lng = userLocation?.lon ?? breederCoords?.lng;
                          if (lat != null && lng != null) {
                              navigate({
                                  to: "/find",
                                  search: {
                                      lat,
                                      lng,
                                      breedSlugs,
                                      maxDistanceMiles:
                                          resolveMaxDistanceMiles(),
                                      page: 0,
                                      limit: 50,
                                  },
                              });
                          } else {
                              navigate({ to: "/find" });
                          }
                      },
                  },
              ]
            : [];

    return (
        <ProviderPageShell
            ref={recordProviderView}
            className={PROVIDER_SHELL_CLASS}
        >
            {(() => (
                <>
                    <span ref={providerViewStatePublication.updateRef} hidden />
                    <span ref={providerViewStatePublication.ownerRef} hidden />
                    <Header
                        className={cn("hidden md:block", PROVIDER_CHROME_CLASS)}
                        data-sheet-chrome
                        hideScrollBorder
                    />

                    <div className="hidden md:block pt-header" />
                    <div
                        data-sheet-breadcrumb
                        className={cn(
                            "hidden md:sticky md:top-17.5 md:z-40 md:block",
                            PROVIDER_BREADCRUMB_CLASS,
                        )}
                    >
                        <Breadcrumb
                            items={[
                                { label: "Find", to: "/find" },
                                ...breedCrumbs,
                                { label: kennelName },
                            ]}
                        />
                    </div>

                    <StickyMobileHeader onBack={handleBack} surface="sheet">
                        <button
                            type="button"
                            aria-label="Share this profile"
                            className={iconButtonClass}
                            onClick={() => shareOrCopy(kennelName)}
                        >
                            <ShareIcon className="size-6" />
                        </button>
                    </StickyMobileHeader>
                </>
            ))()}

            <div className="page-container pt-5.5 pb-12 md:pt-4 md:pb-40 flex flex-col">
                {provider && (
                    <ProviderHeader
                        data={{
                            ...provider,
                            website: providerWebsite,
                            providerType,
                            isVerified,
                            ...headerRating,
                        }}
                    />
                )}
                {claimEntry}

                <div
                    className={cn("mt-10 flex flex-col", PROVIDER_SHEET_CLASS)}
                >
                    <ProviderSheetSentinel />
                    <BreederListingsBlock
                        profile={profile}
                        summaryCards={summaryCards}
                        adoptedCards={adoptedCards}
                        breederName={breederName}
                        providerId={providerId}
                        total={loadedProviderListingPage.total}
                        availableCount={
                            loadedProviderListingPage.availableCount
                        }
                        fallbackImageUrl={profileImageUrl}
                    />

                    {(() => (
                        <>
                            <SectionDivider />
                            <div className={READING_COL}>
                                <MeetTheProviderSection
                                    breederName={breederName}
                                    meetBreederImageUrl={meetBreederImageUrl}
                                    breederDescription={breederDescription}
                                    certifications={certifications}
                                    providerType={providerType}
                                    mediaItems={breederMediaItems}
                                    onPhotoClick={openPhotoCarouselAtIndex}
                                />
                                <ProviderDetailsSection provider={breeder} />
                            </div>

                            {profile && (
                                <>
                                    {trustFactsShownBreeder && (
                                        <>
                                            <SectionDivider />
                                            <div className={READING_COL}>
                                                <TrustFactsSection
                                                    profile={profile}
                                                    excludeCategories={[
                                                        "operations",
                                                    ]}
                                                    askAction={{
                                                        icon: (
                                                            <MascotAvatar
                                                                size={22}
                                                                animated={false}
                                                            />
                                                        ),
                                                        onClick:
                                                            requestOpenChat,
                                                    }}
                                                />
                                            </div>
                                        </>
                                    )}
                                    {pricingShown && (
                                        <>
                                            <SectionDivider />
                                            <div className={READING_COL}>
                                                <PricingSection
                                                    pricing={profile.pricing}
                                                />
                                            </div>
                                        </>
                                    )}
                                </>
                            )}

                            {hasReviewsSection && (
                                <>
                                    <SectionDivider />
                                    <section
                                        id="reviews"
                                        className="flex scroll-mt-24 flex-col"
                                    >
                                        {profile && reputationShown && (
                                            <div className={READING_COL}>
                                                <ReputationThemesSection
                                                    reputation={
                                                        profile.reputation
                                                    }
                                                    storiesBelow={hasReviews}
                                                />
                                            </div>
                                        )}
                                        {reviewsData && hasReviews && (
                                            <div
                                                className={cn(
                                                    reputationShown && "mt-8",
                                                )}
                                            >
                                                <ProviderReviewsSection
                                                    reviews={reviewsData.reviews.map(
                                                        (r) =>
                                                            mapOrgReviewToReview(
                                                                r,
                                                            ),
                                                    )}
                                                    reviewCount={
                                                        reviewsData.total
                                                    }
                                                    organizationId={providerId}
                                                    showHeading={
                                                        !reputationShown
                                                    }
                                                />
                                            </div>
                                        )}
                                    </section>
                                </>
                            )}

                            {reviewsErrorState}
                        </>
                    ))()}

                    {(() => (
                        <>
                            {nearbyProvidersErrorState}

                            {whatToExpectShown && (
                                <>
                                    <SectionDivider />
                                    <div
                                        id="requirements"
                                        ref={requirementsAnchorRef}
                                        className={cn(
                                            READING_COL,
                                            "scroll-mt-24",
                                        )}
                                    >
                                        <WhatToExpectSection
                                            breederName={breederName}
                                            meetupLocationLabel={
                                                meetupLocationLabel
                                            }
                                            distanceLabel={distanceLabel}
                                            staticMapSrc={staticMapSrc}
                                            coordinates={breederCoords}
                                            screeningFacts={screeningFacts}
                                            locations={providerLocations}
                                        />
                                    </div>
                                </>
                            )}

                            {nearbyProvidersData &&
                                nearbyProvidersData.providers.length > 0 && (
                                    <>
                                        <SectionDivider />
                                        <NearbyProvidersSection
                                            providers={
                                                nearbyProvidersData.providers
                                            }
                                            city={
                                                breeder?.city ?? locationDisplay
                                            }
                                        />
                                    </>
                                )}

                            <div className="h-9 md:hidden" />
                        </>
                    ))()}
                </div>
            </div>

            {(() => (
                <>
                    {breederMediaItems.length > 0 && (
                        <ImageCarouselDialog
                            mediaItems={breederMediaItems}
                            open={isPhotoCarouselOpen}
                            onOpenChange={setIsPhotoCarouselOpen}
                            alt={breederName}
                            initialIndex={photoCarouselStartIndex}
                        />
                    )}
                    {copilotEnabled && (
                        <KitsCopilotPageContext
                            contactPreview={{
                                provider: {
                                    name: kennelName,
                                    type: providerType,
                                    isVerified,
                                },
                            }}
                        />
                    )}
                </>
            ))()}
        </ProviderPageShell>
    );
}
